import * as Sentry from '@sentry/react';
import { lazy } from 'react';
import { claimAutomaticReload, forceFullLoad, isUnsavedFormOpen, requestVersionCheck, VERSION_CHECK_STATUS, } from './appVersion';
/** Settles once the caller has to show the failure itself; never settles while a full load replaces the page. */
async function handleLoadFailure(error) {
    const versionCheck = await requestVersionCheck();
    if (versionCheck.status === VERSION_CHECK_STATUS.NEW_VERSION) {
        // A full load would discard the unsaved form; the stale tab loads fully on the next path change instead.
        if (isUnsavedFormOpen()) {
            return;
        }
        if (claimAutomaticReload(versionCheck.version)) {
            forceFullLoad('load-failed');
            // Never settles, so the caller (and any Suspense loading indicator) waits until the reload replaces the page.
            await new Promise(() => undefined);
        }
    }
    Sentry.captureException(error);
}
/**
 * `React.lazy` that, when the import fails while a newer version is deployed, fully loads the page (once per version,
 * never over an unsaved form); any other failure renders `Fallback`.
 */
export function lazyWithVersionCheck(importComponent, Fallback) {
    return lazy(() => importComponent().catch(async (error) => {
        await handleLoadFailure(error);
        return { default: Fallback };
    }));
}
/**
 * Dynamic import that, when it fails while a newer version is deployed, fully loads the page (once per version, never
 * over an unsaved form); any other failure rejects with the original error.
 */
export async function importWithVersionCheck(importModule) {
    try {
        return await importModule();
    }
    catch (error) {
        await handleLoadFailure(error);
        throw error;
    }
}
